import { IInputStep } from 'interfaces/IInputStep';

/**
 * Moves a dragged step to where it's dropped (OP-2534): it lands in the
 * target's place and the steps in between shift up or down by one, rather
 * than the target swapping places with it.
 *
 * Only the steps the user can see move. Steps hidden by a category filter
 * keep their absolute positions: the visible steps are re-ordered among
 * themselves and written back into the same array slots they occupied.
 * Array position is the tank-mix order (see swapStepsById).
 *
 * Returns `null` when nothing would change — either id is absent or hidden,
 * or both name the same step.
 *
 * @param steps - The task's full steps array
 * @param visibleStepIds - Ids of the steps currently shown
 * @param draggedId - The step being moved
 * @param targetId - The step it was dropped on
 */
export const moveVisibleStep = (
  steps: IInputStep[],
  visibleStepIds: ReadonlySet<string>,
  draggedId: string,
  targetId: string
): IInputStep[] | null => {
  if (draggedId === targetId) return null;
  if (!visibleStepIds.has(draggedId) || !visibleStepIds.has(targetId)) {
    return null;
  }

  const visibleSlots: number[] = [];
  const visibleSteps: IInputStep[] = [];
  steps.forEach((step, index) => {
    if (visibleStepIds.has(step.stepId)) {
      visibleSlots.push(index);
      visibleSteps.push(step);
    }
  });

  const from = visibleSteps.findIndex((step) => step.stepId === draggedId);
  const to = visibleSteps.findIndex((step) => step.stepId === targetId);
  if (from === -1 || to === -1) return null;

  const reordered = [...visibleSteps];
  const [dragged] = reordered.splice(from, 1);
  reordered.splice(to, 0, dragged);

  const next = [...steps];
  visibleSlots.forEach((slot, index) => {
    next[slot] = reordered[index];
  });
  return next;
};

export default moveVisibleStep;
